import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, Circle, ChevronRight, X,
  User, Search, Heart, FileText, Send,
  Building2, Upload, Shield, MessageSquare,
  Calendar, Users, Briefcase, TrendingUp,
  DollarSign, List
} from 'lucide-react';

export type OnboardingRole =
  | 'buyer' | 'seller' | 'broker'
  | 'consultant' | 'lender' | 'partner';

interface OnboardingStep {
  id: string;
  titleEn: string;
  titleFr: string;
  descEn: string;
  descFr: string;
  icon: React.ElementType;
  action: {
    type: 'navigate' | 'tab';
    target: string;
  };
  ctaEn: string;
  ctaFr: string;
}

const STEPS: Record<OnboardingRole, OnboardingStep[]> = {
  buyer: [
    {
      id: 'profile',
      titleEn: 'Complete your profile',
      titleFr: 'Compléter votre profil',
      descEn: 'Add your acquisition criteria, budget range, and experience to attract sellers.',
      descFr: 'Ajoutez vos critères d\'acquisition, budget et expérience pour attirer les vendeurs.',
      icon: User,
      action: { type: 'navigate', target: '/buyer/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'explore',
      titleEn: 'Explore opportunities',
      titleFr: 'Explorer les opportunités',
      descEn: 'Browse anonymized business listings on the Deal Monitor.',
      descFr: 'Parcourez les annonces anonymisées sur le Deal Monitor.',
      icon: Search,
      action: { type: 'navigate', target: '/listings' },
      ctaEn: 'Browse Listings',
      ctaFr: 'Voir les annonces',
    },
    {
      id: 'favorites',
      titleEn: 'Save your favorites',
      titleFr: 'Sauvegarder vos favoris',
      descEn: 'Add deals to your favorites to track them and compare later.',
      descFr: 'Ajoutez des deals à vos favoris pour les suivre et les comparer.',
      icon: Heart,
      action: { type: 'tab', target: 'favorites' },
      ctaEn: 'View Favorites',
      ctaFr: 'Voir les favoris',
    },
    {
      id: 'nda',
      titleEn: 'Sign an NDA',
      titleFr: 'Signer un NDA',
      descEn: 'Sign a Non-Disclosure Agreement to access full financial data of a business.',
      descFr: 'Signez un accord de confidentialité pour accéder aux données financières complètes.',
      icon: Shield,
      action: { type: 'tab', target: 'nda' },
      ctaEn: 'View NDA Status',
      ctaFr: 'Voir statut NDA',
    },
    {
      id: 'loi',
      titleEn: 'Submit a Letter of Intent',
      titleFr: 'Soumettre une Lettre d\'Intention',
      descEn: 'Once your NDA is signed, submit an LOI to express your acquisition interest.',
      descFr: 'Une fois votre NDA signé, soumettez une LOI pour exprimer votre intérêt.',
      icon: Send,
      action: { type: 'navigate', target: '/tools/loi-generator' },
      ctaEn: 'Create LOI',
      ctaFr: 'Créer une LOI',
    },
  ],
  seller: [
    {
      id: 'profile',
      titleEn: 'Complete your profile',
      titleFr: 'Compléter votre profil',
      descEn: 'A complete profile builds trust with buyers and lenders.',
      descFr: 'Un profil complet inspire confiance aux acheteurs et prêteurs.',
      icon: User,
      action: { type: 'navigate', target: '/seller/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'submit',
      titleEn: 'Submit your first deal',
      titleFr: 'Soumettre votre premier deal',
      descEn: 'Complete the 12-step deal submission form to list your business.',
      descFr: 'Complétez le formulaire de soumission en 12 étapes pour lister votre entreprise.',
      icon: Building2,
      action: { type: 'tab', target: 'submit-deal' },
      ctaEn: 'Submit Deal',
      ctaFr: 'Soumettre un deal',
    },
    {
      id: 'documents',
      titleEn: 'Upload financial documents',
      titleFr: 'Téléverser vos documents financiers',
      descEn: 'Upload P&L, Balance Sheet, and Tax Reports to your secure Data Room.',
      descFr: 'Téléversez P&L, Bilan et Rapports d\'impôts dans votre Data Room sécurisée.',
      icon: Upload,
      action: { type: 'tab', target: 'my-deals' },
      ctaEn: 'Go to My Deals',
      ctaFr: 'Aller à mes deals',
    },
    {
      id: 'impact',
      titleEn: 'Update your Impact data',
      titleFr: 'Mettre à jour vos données Impact',
      descEn: 'Declare your workforce and sustainability data to attract responsible buyers.',
      descFr: 'Déclarez vos données main-d\'œuvre et durabilité pour attirer des acheteurs responsables.',
      icon: TrendingUp,
      action: { type: 'tab', target: 'my-deals' },
      ctaEn: 'Update Impact',
      ctaFr: 'Mettre à jour Impact',
    },
    {
      id: 'contacts',
      titleEn: 'Manage contact requests',
      titleFr: 'Gérer les demandes de contact',
      descEn: 'Review buyer profiles and accept or decline contact requests.',
      descFr: 'Consultez les profils acheteurs et acceptez ou refusez les demandes de contact.',
      icon: MessageSquare,
      action: { type: 'tab', target: 'messages' },
      ctaEn: 'View Messages',
      ctaFr: 'Voir les messages',
    },
  ],
  broker: [
    {
      id: 'profile',
      titleEn: 'Complete your profile',
      titleFr: 'Compléter votre profil',
      descEn: 'A complete broker profile builds credibility with sellers and buyers.',
      descFr: 'Un profil courtier complet renforce votre crédibilité.',
      icon: User,
      action: { type: 'navigate', target: '/dashboard/broker/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'seller',
      titleEn: 'Add a represented seller',
      titleFr: 'Ajouter un vendeur représenté',
      descEn: 'Register the sellers you represent before submitting their deals.',
      descFr: 'Enregistrez les vendeurs que vous représentez avant de soumettre leurs deals.',
      icon: Users,
      action: { type: 'tab', target: 'sellers' },
      ctaEn: 'Add Seller',
      ctaFr: 'Ajouter un vendeur',
    },
    {
      id: 'deal',
      titleEn: 'Submit a deal',
      titleFr: 'Soumettre un deal',
      descEn: 'List a business on behalf of your represented seller.',
      descFr: 'Listez une entreprise au nom de votre vendeur représenté.',
      icon: Building2,
      action: { type: 'tab', target: 'list-business' },
      ctaEn: 'Submit Deal',
      ctaFr: 'Soumettre un deal',
    },
    {
      id: 'contacts',
      titleEn: 'Manage contact requests',
      titleFr: 'Gérer les demandes de contact',
      descEn: 'Review buyer profiles and coordinate with your represented sellers.',
      descFr: 'Consultez les profils acheteurs et coordonnez avec vos vendeurs représentés.',
      icon: MessageSquare,
      action: { type: 'tab', target: 'contact-requests' },
      ctaEn: 'View Requests',
      ctaFr: 'Voir les demandes',
    },
    {
      id: 'listings',
      titleEn: 'Track your listings',
      titleFr: 'Suivre vos listings',
      descEn: 'Monitor the performance and status of all your active listings.',
      descFr: 'Suivez la performance et le statut de tous vos listings actifs.',
      icon: List,
      action: { type: 'tab', target: 'my-listings' },
      ctaEn: 'View Listings',
      ctaFr: 'Voir les listings',
    },
  ],
  consultant: [
    {
      id: 'profile',
      titleEn: 'Complete your profile',
      titleFr: 'Compléter votre profil',
      descEn: 'Add your specialties, experience, and credentials to attract mandates.',
      descFr: 'Ajoutez vos spécialités, expérience et accréditations pour attirer des mandats.',
      icon: User,
      action: { type: 'navigate', target: '/consultant/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'calendar',
      titleEn: 'Set up your calendar',
      titleFr: 'Configurer votre calendrier',
      descEn: 'Add your availability so buyers and sellers can schedule meetings with you.',
      descFr: 'Ajoutez vos disponibilités pour que les acheteurs et vendeurs puissent vous rejoindre.',
      icon: Calendar,
      action: { type: 'navigate', target: '/dashboard/consultant/calendar' },
      ctaEn: 'Open Calendar',
      ctaFr: 'Ouvrir le calendrier',
    },
    {
      id: 'client',
      titleEn: 'Add your first client',
      titleFr: 'Ajouter votre premier client',
      descEn: 'Register a buyer or seller you are working with.',
      descFr: 'Enregistrez un acheteur ou vendeur avec qui vous travaillez.',
      icon: Users,
      action: { type: 'navigate', target: '/consultant/add-client' },
      ctaEn: 'Add Client',
      ctaFr: 'Ajouter un client',
    },
    {
      id: 'mandate',
      titleEn: 'Create your first mandate',
      titleFr: 'Créer votre premier mandat',
      descEn: 'Formalize your engagement with a client through a mandate.',
      descFr: 'Formalisez votre engagement avec un client via un mandat.',
      icon: Briefcase,
      action: { type: 'navigate', target: '/consultant/new-mandate' },
      ctaEn: 'Create Mandate',
      ctaFr: 'Créer un mandat',
    },
    {
      id: 'listings',
      titleEn: 'Explore deals in your sector',
      titleFr: 'Explorer les deals dans votre secteur',
      descEn: 'Browse the Deal Monitor to identify opportunities for your clients.',
      descFr: 'Parcourez le Deal Monitor pour identifier des opportunités pour vos clients.',
      icon: Search,
      action: { type: 'navigate', target: '/listings' },
      ctaEn: 'Browse Listings',
      ctaFr: 'Voir les annonces',
    },
  ],
  lender: [
    {
      id: 'profile',
      titleEn: 'Complete your lender profile',
      titleFr: 'Compléter votre profil prêteur',
      descEn: 'Add your institution details, financing criteria, and coverage regions.',
      descFr: 'Ajoutez les détails de votre institution, critères de financement et régions.',
      icon: User,
      action: { type: 'navigate', target: '/lender/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'calendar',
      titleEn: 'Set up your calendar',
      titleFr: 'Configurer votre calendrier',
      descEn: 'Add your availability for buyer meetings after LOI execution.',
      descFr: 'Ajoutez vos disponibilités pour les réunions acheteurs après signature de la LOI.',
      icon: Calendar,
      action: { type: 'navigate', target: '/dashboard/lender/calendar' },
      ctaEn: 'Open Calendar',
      ctaFr: 'Ouvrir le calendrier',
    },
    {
      id: 'explore',
      titleEn: 'Explore financing opportunities',
      titleFr: 'Explorer les opportunités de financement',
      descEn: 'Browse deals seeking financing — anonymized until LOI execution.',
      descFr: 'Parcourez les deals cherchant du financement — anonymisés jusqu\'à la LOI.',
      icon: Search,
      action: { type: 'navigate', target: '/dashboard/lender/loan-offers' },
      ctaEn: 'Browse Deals',
      ctaFr: 'Voir les deals',
    },
    {
      id: 'offer',
      titleEn: 'Submit your first offer',
      titleFr: 'Soumettre votre première offre',
      descEn: 'Review deal details and submit a financing offer anonymously.',
      descFr: 'Consultez les détails du deal et soumettez une offre de financement anonymement.',
      icon: DollarSign,
      action: { type: 'navigate', target: '/lender/submit-offer' },
      ctaEn: 'Submit Offer',
      ctaFr: 'Soumettre une offre',
    },
    {
      id: 'pipeline',
      titleEn: 'Track your pipeline',
      titleFr: 'Suivre votre pipeline',
      descEn: 'Monitor the status of your submitted offers and active loans.',
      descFr: 'Suivez le statut de vos offres soumises et prêts actifs.',
      icon: TrendingUp,
      action: { type: 'navigate', target: '/dashboard/lender/pipeline' },
      ctaEn: 'View Pipeline',
      ctaFr: 'Voir le pipeline',
    },
  ],
  partner: [
    {
      id: 'profile',
      titleEn: 'Complete your public profile',
      titleFr: 'Compléter votre profil public',
      descEn: 'Add your specialties, credentials, and service regions to be found by users.',
      descFr: 'Ajoutez vos spécialités, accréditations et régions de service pour être trouvé.',
      icon: User,
      action: { type: 'navigate', target: '/partners/profile' },
      ctaEn: 'Go to Profile',
      ctaFr: 'Aller au profil',
    },
    {
      id: 'calendar',
      titleEn: 'Add your availability',
      titleFr: 'Ajouter vos disponibilités',
      descEn: 'Keep your calendar up to date so users can book appointments with you.',
      descFr: 'Maintenez votre calendrier à jour pour que les usagers puissent prendre rendez-vous.',
      icon: Calendar,
      action: { type: 'navigate', target: '/internal-partners/business-law/calendar' },
      ctaEn: 'Open Calendar',
      ctaFr: 'Ouvrir le calendrier',
    },
    {
      id: 'region',
      titleEn: 'Define your service region',
      titleFr: 'Définir votre région de service',
      descEn: 'Set your geographic coverage to receive deal notifications in your area.',
      descFr: 'Définissez votre couverture géographique pour recevoir des notifications de deals.',
      icon: Search,
      action: { type: 'navigate', target: '/partners/profile' },
      ctaEn: 'Update Profile',
      ctaFr: 'Mettre à jour le profil',
    },
    {
      id: 'listings',
      titleEn: 'Explore active deals',
      titleFr: 'Explorer les deals actifs',
      descEn: 'Browse the Deal Monitor to familiarize yourself with available opportunities.',
      descFr: 'Parcourez le Deal Monitor pour vous familiariser avec les opportunités disponibles.',
      icon: FileText,
      action: { type: 'navigate', target: '/listings' },
      ctaEn: 'Browse Listings',
      ctaFr: 'Voir les annonces',
    },
    {
      id: 'messages',
      titleEn: 'Use internal messaging',
      titleFr: 'Utiliser la messagerie interne',
      descEn: 'Contact sellers and brokers directly through the secure platform messaging.',
      descFr: 'Contactez vendeurs et courtiers via la messagerie sécurisée de la plateforme.',
      icon: MessageSquare,
      action: { type: 'navigate', target: '/internal-partners/business-law/messages' },
      ctaEn: 'Open Messages',
      ctaFr: 'Ouvrir les messages',
    },
  ],
};

interface Props {
  role: OnboardingRole;
  language: 'en' | 'fr';
  completedStepIds?: string[];
  onTabChange?: (tab: string) => void;
  partnerType?: string;
}

export function OnboardingGuide({
  role,
  language,
  completedStepIds = [],
  onTabChange,
  partnerType,
}: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [localCompleted, setLocalCompleted] =
    useState(completedStepIds);

  const steps = STEPS[role] || STEPS.buyer;
  const completed = localCompleted.length;
  const total = steps.length;
  const isEn = language === 'en';

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const snap = await getDoc(
          doc(db, 'users', user.id, 'onboarding', role)
        );
        if (snap.exists()) {
          const data = snap.data();
          if (data.dismissed) setDismissed(true);
          if (data.completedSteps) setLocalCompleted(data.completedSteps);
        }
      } catch {}
    };
    load();
  }, [user, role]);

  const handleDismiss = async () => {
    setDismissed(true);
    if (!user) return;
    try {
      await setDoc(
        doc(db, 'users', user.id, 'onboarding', role),
        { dismissed: true, dismissedAt: serverTimestamp() },
        { merge: true }
      );
    } catch {}
  };

  const handleStepAction = async (step: OnboardingStep) => {
    // Mark step as completed
    const newCompleted = localCompleted.includes(step.id)
      ? localCompleted
      : [...localCompleted, step.id];
    setLocalCompleted(newCompleted);

    if (user) {
      try {
        await setDoc(
          doc(db, 'users', user.id, 'onboarding', role),
          { completedSteps: newCompleted, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch {}
    }

    // Execute action
    if (step.action.type === 'navigate') {
      let target = step.action.target;
      // Replace {type} placeholder for partner routes
      if (partnerType) {
        target = target.replace('{type}', partnerType);
      }
      navigate(target);
    } else if (step.action.type === 'tab' && onTabChange) {
      onTabChange(step.action.target);
    }
  };

  if (dismissed || completed >= total) return null;

  const progressPct = Math.round((completed / total) * 100);

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-foreground">
              {isEn ? 'Getting Started' : 'Premiers pas'}
            </h3>
            <Badge variant="outline" className="text-xs">
              {completed}/{total}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {isEn
              ? 'Complete these steps to get the most out of AcquizGroup.'
              : 'Complétez ces étapes pour tirer le meilleur d\'AcquizGroup.'}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-1.5 text-right text-xs text-muted-foreground">
          {progressPct}% {isEn ? 'complete' : 'complété'}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const isDone = localCompleted.includes(step.id);
          const Icon = step.icon;
          const isNext = !isDone &&
            steps.slice(0, idx).every(s => localCompleted.includes(s.id));

          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                isDone
                  ? 'border-border/50 bg-muted/30 opacity-70'
                  : isNext
                  ? 'border-primary/30 bg-primary/5'
                  : 'border-border bg-card'
              }`}
            >
              {/* Status icon */}
              {isDone ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
              ) : (
                <Circle className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}

              {/* Step icon */}
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                isDone ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon className="h-4 w-4" />
              </div>

              {/* Text */}
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${isDone ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                  {isEn ? step.titleEn : step.titleFr}
                </p>
                {isNext && !isDone && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {isEn ? step.descEn : step.descFr}
                  </p>
                )}
              </div>

              {/* CTA */}
              {isNext && !isDone && (
                <Button
                  onClick={() => handleStepAction(step)}
                  className="shrink-0 bg-[#1D314D] hover:bg-[#2E4663] text-white gap-1 text-xs h-8"
                >
                  {isEn ? step.ctaEn : step.ctaFr}
                  <ChevronRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
